import { useState, useEffect } from 'react';
import vibration from '@/assets/mobile-notch.svg';
import soundSvg from '@/assets/sound.svg';
import { useFirebaseFunction } from '@/hooks/useFirebaseFunction';
import { callFirebaseFunction } from '@/bridge/callFirebaseFunction';
import { usePersistentDataState } from '@/hooks/usePersistentDataState';

// Define types for our API calls
type GetUserPrivateInfoRequest = {
  fields?: string[];
};

type GetUserPrivateInfoResponse = {
  userInfo: {
    soundEffectsEnabled?: boolean;
    hapticsEnabled?: boolean;
  };
};

type UpdateUserPrivateInfoRequest = {
  updates: Partial<{
    soundEffectsEnabled?: boolean;
    hapticsEnabled?: boolean;
  }>;
};

type UpdateUserPrivateInfoResponse = {
  success: boolean;
};

export function ExperienceSettings() {
    // Get persistent state setters
    const [soundEffectsEnabled, setSoundEffectsEnabled] = usePersistentDataState("soundEffectsEnabled", true);
    const [vibrationEnabled, setVibrationEnabled] = usePersistentDataState("vibrationEnabled", true);
    
    // State for tracking initial values from Firebase
    const [initialSoundEffects, setInitialSoundEffects] = useState(soundEffectsEnabled);
    const [initialPhoneVibrations, setInitialPhoneVibrations] = useState(vibrationEnabled);
    
    // Use hook to fetch experience settings
    const [userData, isLoading, fetchError] = useFirebaseFunction<
      GetUserPrivateInfoRequest, 
      GetUserPrivateInfoResponse
    >(
      'getUserPrivateInfo',
      (fn) => fn({ fields: ["soundEffectsEnabled", "hapticsEnabled"] }),
      []
    );
    
    // Set initial values when data is loaded
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (userData && !isLoading) {
            console.log('[getRequest] ', userData);
            const fetchedSoundEffects = userData.userInfo.soundEffectsEnabled ?? false;
            const fetchedHaptics = userData.userInfo.hapticsEnabled ?? false;
            
            // Update persistent state to match Firebase values
            setSoundEffectsEnabled(fetchedSoundEffects);
            setVibrationEnabled(fetchedHaptics);
            
            // Store initial values for revert if needed
            setInitialSoundEffects(fetchedSoundEffects);
            setInitialPhoneVibrations(fetchedHaptics);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, isLoading]);
    
    // Show error toast if fetch fails
    useEffect(() => {
        if (fetchError) {
            // toast({
            //     title: "Error",
            //     description: "Failed to load experience settings. Please try again.",
            //     variant: "destructive"
            // });
        }
    }, [fetchError]);
    
    // Handle sound effects toggle
    const handleSoundEffectsToggle = async () => {
        const newValue = !soundEffectsEnabled;
        
        // Optimistically update persistent state
        setSoundEffectsEnabled(newValue);
        
        try {
            // Call the update function directly
            const response = await callFirebaseFunction<
              UpdateUserPrivateInfoRequest,
              UpdateUserPrivateInfoResponse
            >(
              "updateUserPrivateInfo",
              { 
                updates: { 
                  soundEffectsEnabled: newValue 
                } 
              }
            );
            
            if (response.success) {
                // Update initial value to match current
                setInitialSoundEffects(newValue);
                // toast({
                //     title: "Success",
                //     description: `Sound effects ${newValue ? 'enabled' : 'disabled'}.`,
                // });
            }
        } catch (error) {
            // Revert to initial value if update fails
            setSoundEffectsEnabled(initialSoundEffects || true);
            console.error("Error updating sound effects:", error);
            // toast({
            //     title: "Error",
            //     description: "Failed to update sound effects settings.",
            //     variant: "destructive"
            // });
        }
    };
    
    // Handle phone vibrations toggle
    const handlePhoneVibrationsToggle = async () => {
        const newValue = !vibrationEnabled;
        
        // Optimistically update persistent state
        setVibrationEnabled(newValue);
        
        try {
            // Call the update function directly
            const response = await callFirebaseFunction<
              UpdateUserPrivateInfoRequest,
              UpdateUserPrivateInfoResponse
            >(
              "updateUserPrivateInfo",
              { 
                updates: { 
                  hapticsEnabled: newValue 
                } 
              }
            );
            
            if (response.success) {
                // Update initial value to match current
                setInitialPhoneVibrations(newValue);
                // toast({
                //     title: "Success",
                //     description: `Phone vibrations ${newValue ? 'enabled' : 'disabled'}.`,
                // });
            }
        } catch (error) {
            // Revert to initial value if update fails
            setVibrationEnabled(initialPhoneVibrations || true);
            console.error("Error updating phone vibrations:", error);
            // toast({
            //     title: "Error",
            //     description: "Failed to update phone vibrations settings.",
            //     variant: "destructive"
            // });
        }
    };
    
    return (
        <div className="mb-8">
            <h2 className="text-2xl font-bold text-blue-dark mb-6">Experience settings</h2>
            
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-blue border-t-transparent rounded-full" />
                </div>
            ) : (
                <>
                    <div className="border border-slate-200 rounded p-4 mb-3 flex items-center justify-between">
                        <div className="flex items-center">
                            <img src={soundSvg} className='h-6 mr-3' alt='sound effects'/>
                            <div>
                                <div className="font-medium text-blue-dark">Sound effects</div>
                                <div className="text-xs text-slate">Sound effects while playing</div>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="sr-only peer"
                                checked={soundEffectsEnabled}
                                onChange={handleSoundEffectsToggle}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-400" />
                        </label>
                    </div>
                    
                    <div className="border border-slate-200 rounded p-4 flex items-center justify-between">
                        <div className="flex items-center">
                            <img src={vibration} className='h-6 mr-3' alt='vibrations'/>
                            <div>
                                <div className="font-medium text-blue-dark">Phone vibrations</div>
                                <div className="text-xs text-slate">Phone Vibrations while playing</div>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="sr-only peer"
                                checked={vibrationEnabled}
                                onChange={handlePhoneVibrationsToggle}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-400" />
                        </label>
                    </div>
                    {fetchError && !fetchError?.message.includes('found') && (
                        <p className="text-red-500 text-sm mt-2.5 font-medium">{fetchError.message}</p>
                    )}
                </>
            )}
        </div>
    );
}